/**
 * カラートークンページ
 *
 * Semantic / Primitive / Brand Identity の 3 グループのスウォッチグリッド (クリックで
 * HEX コピー) と色ルールカード。canEdit はトークンの追加・編集・削除ができる。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business カラートークンのスウォッチグリッド表示・HEXコピー・追加・編集・削除と色ルール表示。
 * @graph-connects api [calls] colors.listTokens / upsertToken / deleteToken / listRules
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { ColorGroup, ColorToken } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { useBrandScope } from '../components/brand-scope-context.js';
import { copyText } from '../lib/clipboard.js';
import { brandNameOf, matchesBrandScope } from '../lib/brand-scope.js';
import { COLOR_GROUP_LABELS } from '../lib/labels.js';
import {
  BrandAttributionField,
  BrandBadge,
  EmptyState,
  Field,
  GhostButton,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
  StatusPill,
} from '../components/ui.js';

/** トークン編集ドラフト (新規は id: null、brandId '' = 会社共通)。 */
interface TokenDraft {
  id: string | null;
  name: string;
  hex: string;
  group: ColorGroup;
  note: string;
  brandId: string;
  order: number;
}

/** グループの表示順。 @graph-connects none */
const GROUP_ORDER: ColorGroup[] = ['semantic', 'primitive', 'brand_identity'];

/**
 * カラートークンページ。
 * @returns ページ要素
 * @graph-connects api [calls] colors.listTokens / upsertToken / deleteToken / listRules
 */
export function ColorsPage(): JSX.Element {
  const auth = useAuth();
  const brandScope = useBrandScope();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const tokensQuery = useQuery(orpc.colors.listTokens.queryOptions({ input: {} }));
  const rulesQuery = useQuery(orpc.colors.listRules.queryOptions({ input: {} }));
  const tokens = (tokensQuery.data?.items ?? []).filter((token) =>
    matchesBrandScope(token.brandId, brandScope.scope),
  );
  const rules = (rulesQuery.data?.items ?? []).filter((rule) =>
    matchesBrandScope(rule.brandId, brandScope.scope),
  );

  const [draft, setDraft] = useState<TokenDraft | null>(null);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: orpc.colors.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.guide.key() });
  };

  const upsertMutation = useMutation(
    orpc.colors.upsertToken.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        setDraft(null);
        showToast('success', 'カラートークンを保存しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );
  const deleteMutation = useMutation(
    orpc.colors.deleteToken.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        setDraft(null);
        showToast('success', 'カラートークンを削除しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const copyHex = async (hex: string): Promise<void> => {
    const copied = await copyText(hex);
    showToast(
      copied ? 'success' : 'error',
      copied ? `${hex} をコピーしました` : 'コピーに失敗しました',
    );
  };

  const openEditor = (token: ColorToken | null, group: ColorGroup): void => {
    setDraft(
      token
        ? {
            id: token.id,
            name: token.name,
            hex: token.hex,
            group: token.group,
            note: token.note ?? '',
            brandId: token.brandId ?? '',
            order: token.order,
          }
        : {
            id: null,
            name: '',
            hex: '#',
            group,
            note: '',
            brandId: brandScope.scope,
            order: tokens.length,
          },
    );
  };

  return (
    <div>
      <SectionHeading title="Color Tokens" />
      {tokens.length === 0 && <EmptyState message="カラートークンが未登録です (seed 未実行)" />}
      {GROUP_ORDER.map((group) => {
        const groupTokens = tokens.filter((token) => token.group === group);
        if (groupTokens.length === 0) {
          return null;
        }
        return (
          <div key={group} className="mb-7">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-foreground">{COLOR_GROUP_LABELS[group]}</h3>
              {auth.canEdit && (
                <GhostButton
                  onClick={() => {
                    openEditor(null, group);
                  }}
                >
                  + 追加
                </GhostButton>
              )}
            </div>
            <div className="swatch-grid">
              {groupTokens.map((token) => (
                <div
                  key={token.id}
                  className="group flex items-center gap-2.5 rounded-lg border border-glass-line bg-card px-3.5 py-2.5 transition hover:-translate-y-px hover:border-line"
                >
                  <button
                    type="button"
                    aria-label={`${token.hex} をコピー`}
                    onClick={() => {
                      void copyHex(token.hex);
                    }}
                    className="h-8 w-8 shrink-0 cursor-pointer rounded-lg swatch"
                    style={{ background: token.hex }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-foreground">
                      {token.name}
                      {token.brandId !== undefined && (
                        <BrandBadge label={brandNameOf(brandScope.brands, token.brandId)} />
                      )}
                    </div>
                    <div className="font-mono text-caption text-muted">{token.hex}</div>
                  </div>
                  {auth.canEdit && (
                    <button
                      type="button"
                      className="hidden text-micro text-muted hover:text-foreground group-hover:block"
                      onClick={() => {
                        openEditor(token, group);
                      }}
                    >
                      編集
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {rules.length > 0 && (
        <>
          <h3 className="mb-3 text-sm font-bold text-foreground">色ルール</h3>
          {rules.map((rule) => (
            <div key={rule.id} className="rule-card">
              <span
                className="rule-bar"
                style={{
                  background: rule.level === 'ng' ? 'var(--dh-ng)' : 'var(--dh-caution)',
                }}
              />
              <div className="flex items-start gap-2">
                <StatusPill level={rule.level} />
                <span>{rule.text}</span>
              </div>
              {rule.relatedHexes.length > 0 && (
                <div className="mt-2 flex gap-1.5">
                  {rule.relatedHexes.map((hex) => (
                    <span
                      key={hex}
                      className="inline-flex items-center gap-1 rounded border border-line-light bg-surface px-1.5 py-0.5 font-mono text-micro text-muted"
                    >
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: hex }} />
                      {hex}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      <Modal
        open={draft !== null}
        title={draft?.id === null ? 'カラートークンを追加' : 'カラートークンを編集'}
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="名前">
              <input
                className={INPUT_CLASS}
                value={draft.name}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value });
                }}
              />
            </Field>
            <Field label="HEX (#RRGGBB)">
              <div className="flex items-center gap-2">
                <input
                  className={INPUT_CLASS}
                  value={draft.hex}
                  onChange={(event) => {
                    setDraft({ ...draft, hex: event.target.value });
                  }}
                />
                <span
                  className="h-9 w-9 shrink-0 rounded-lg border border-line"
                  style={{ background: draft.hex }}
                />
              </div>
            </Field>
            <Field label="補足 (任意)">
              <input
                className={INPUT_CLASS}
                value={draft.note}
                onChange={(event) => {
                  setDraft({ ...draft, note: event.target.value });
                }}
              />
            </Field>
            <BrandAttributionField
              brands={brandScope.brands}
              value={draft.brandId}
              onChange={(brandId) => {
                setDraft({ ...draft, brandId });
              }}
            />
            <div className="mt-4 flex items-center justify-between">
              {draft.id !== null ? (
                <button
                  type="button"
                  className="text-xs text-ng opacity-70 hover:opacity-100"
                  onClick={() => {
                    if (draft.id !== null) {
                      deleteMutation.mutate({ id: draft.id });
                    }
                  }}
                >
                  削除
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <GhostButton
                  onClick={() => {
                    setDraft(null);
                  }}
                >
                  キャンセル
                </GhostButton>
                <PrimaryButton
                  disabled={
                    draft.name === '' ||
                    !/^#[0-9A-Fa-f]{6}$/.test(draft.hex) ||
                    upsertMutation.isPending
                  }
                  onClick={() => {
                    upsertMutation.mutate({
                      id: draft.id,
                      input: {
                        name: draft.name,
                        hex: draft.hex,
                        group: draft.group,
                        note: draft.note === '' ? undefined : draft.note,
                        brandId: draft.brandId === '' ? undefined : draft.brandId,
                        order: draft.order,
                      },
                    });
                  }}
                >
                  保存
                </PrimaryButton>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
